import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { OrderTakenBy } from "@school-sales-ai/shared";
import type { ViewMode } from "@/stores/crm.store";

type TextFilterKey = "search" | "from" | "to";

/**
 * The /pedidos filter bar and view mode.
 *
 * Its own store, not `crm.store`: the two pages filter different things, and a
 * search typed in Prospectos must never narrow Pedidos behind someone's back.
 * The shape follows `crm.store` on purpose — filters live in memory, only the
 * view mode persists.
 *
 * Every categorical filter is a LIST. Values inside one are OR'd and separate
 * filters are AND'd, which is what the backend's `buildOrderListWhere` does.
 */
type OrdersState = {
  search: string;
  from: string;
  to: string;
  /** Board column keys: a status, or `stage:<id>`. */
  columns: string[];
  takenBy: OrderTakenBy[];
  productIds: string[];
  page: number;

  setFilter: (key: TextFilterKey, value: string) => void;
  toggleColumn: (key: string) => void;
  /** Replaces the selection — a column's "Ver todos en tabla". */
  setColumns: (keys: string[]) => void;
  clearColumns: () => void;
  toggleTakenBy: (who: OrderTakenBy) => void;
  clearTakenBy: () => void;
  toggleProduct: (id: string) => void;
  clearProducts: () => void;
  setPage: (page: number) => void;
  reset: () => void;

  /** How many cards each board column has loaded past the first page. */
  columnLimits: Record<string, number>;
  growColumn: (key: string, by: number) => void;

  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
};

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// What "Limpiar filtros" clears. The view mode stays: clearing a search should
// not flip the board out from under someone.
const FILTER_DEFAULTS = {
  search: "",
  from: "",
  to: "",
  columns: [] as string[],
  takenBy: [] as OrderTakenBy[],
  productIds: [] as string[],
  page: 1,
  // A limit grown against one result set means nothing against the next.
  columnLimits: {} as Record<string, number>,
};

export const useOrdersStore = create<OrdersState>()(
  persist(
    (set) => ({
      ...FILTER_DEFAULTS,

      setFilter: (key, value) => set({ [key]: value, page: 1, columnLimits: {} }),
      toggleColumn: (key) => set((s) => ({ columns: toggle(s.columns, key), page: 1 })),
      setColumns: (keys) => set({ columns: keys, page: 1 }),
      clearColumns: () => set({ columns: [], page: 1 }),
      toggleTakenBy: (who) =>
        set((s) => ({ takenBy: toggle(s.takenBy, who), page: 1, columnLimits: {} })),
      clearTakenBy: () => set({ takenBy: [], page: 1, columnLimits: {} }),
      toggleProduct: (id) =>
        set((s) => ({ productIds: toggle(s.productIds, id), page: 1, columnLimits: {} })),
      clearProducts: () => set({ productIds: [], page: 1, columnLimits: {} }),
      setPage: (page) => set({ page }),
      reset: () => set(FILTER_DEFAULTS),

      growColumn: (key, by) =>
        set((s) => ({
          columnLimits: { ...s.columnLimits, [key]: (s.columnLimits[key] ?? by) + by },
        })),

      viewMode: "board",
      setViewMode: (mode) => set({ viewMode: mode }),
    }),
    {
      name: "orders-view-preferences",
      partialize: (s) => ({ viewMode: s.viewMode }),
    },
  ),
);

/** True when any filter is narrowing the result set. */
export function hasActiveOrderFilters(s: OrdersState): boolean {
  return (
    !!s.search ||
    !!s.from ||
    !!s.to ||
    s.columns.length > 0 ||
    s.takenBy.length > 0 ||
    s.productIds.length > 0
  );
}
